import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  InputBase,
  alpha,
  styled,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Drawer,
  List,
  ListItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import CategoryIcon from '@mui/icons-material/Category';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import TimelineIcon from '@mui/icons-material/Timeline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PeopleIcon from '@mui/icons-material/People';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import MenuIcon from '@mui/icons-material/Menu';
import SearchBar from './SearchBar';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '40ch',
    },
  },
}));

function Navbar() {
  const navigate = useNavigate();
  const [exploreAnchor, setExploreAnchor] = useState(null);
  const [statsAnchor, setStatsAnchor] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleExploreClick = (event) => {
    setExploreAnchor(event.currentTarget);
  };

  const handleExploreClose = () => {
    setExploreAnchor(null);
  };

  const handleStatsClick = (event) => {
    setStatsAnchor(event.currentTarget);
  };

  const handleStatsClose = () => {
    setStatsAnchor(null);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const exploreItems = [
    { label: 'Collections', icon: <CollectionsBookmarkIcon />, path: '/collections' },
    { label: 'Categories', icon: <CategoryIcon />, path: '/categories' },
    { label: 'Rankings', icon: <LeaderboardIcon />, path: '/rankings' },
    { label: 'New & Notable', icon: <NewReleasesIcon />, path: '/new' },
    { label: 'Drops', icon: <LocalActivityIcon />, path: '/drops' },
    { label: 'Activity', icon: <TimelineIcon />, path: '/activity' },
  ];

  const statsItems = [
    { label: 'Market Overview', icon: <ShowChartIcon />, path: '/stats/market' },
    { label: 'Price History', icon: <TimelineIcon />, path: '/stats/price-history' },
    { label: 'Top Sales', icon: <MonetizationOnIcon />, path: '/stats/top-sales' },
    { label: 'Analytics', icon: <BarChartIcon />, path: '/stats/analytics' },
    { label: 'Leaderboard', icon: <PeopleIcon />, path: '/stats/leaderboard' },
    { label: 'Gas Tracker', icon: <LocalGasStationIcon />, path: '/stats/gas' },
  ];

  const mobileMenuItems = [
    { label: 'Explore', items: exploreItems },
    { label: 'Stats', items: statsItems },
  ];

  const MenuProps = {
    PaperProps: {
      sx: {
        mt: 1.5,
        minWidth: 200,
        background: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        '& .MuiMenuItem-root': {
          px: 2,
          py: 1.5,
          borderRadius: 1,
          cursor: 'default',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    transformOrigin: { horizontal: 'center', vertical: 'top' },
    anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
  };

  return (
    <AppBar position="sticky" sx={{ backgroundColor: 'background.paper' }}>
      <Toolbar>
        <RouterLink to="/" style={{ textDecoration: 'none' }}>
          <Box
            component="img"
            src="/logo.png"
            alt="CastleNFTExchange"
            sx={{
              height: { xs: '30px', sm: '40px' },
              width: 'auto',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                transform: 'scale(1.05)',
              }
            }}
          />
        </RouterLink>

        {!isMobile && (
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <SearchBar StyledInputBase={StyledInputBase} />
          </Search>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {isMobile ? (
          <IconButton
            color="inherit"
            edge="end"
            onClick={toggleMobileMenu}
            sx={{ color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>
        ) : (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              color="inherit"
              onClick={handleExploreClick}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              Explore
            </Button>
            <Menu
              anchorEl={exploreAnchor}
              open={Boolean(exploreAnchor)}
              onClose={handleExploreClose}
              {...MenuProps}
            >
              {exploreItems.map((item) => (
                <MenuItem 
                  key={item.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ color: 'primary.main' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label}
                    primaryTypographyProps={{
                      sx: { 
                        color: 'text.primary',
                        fontWeight: 500,
                      }
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>

            <Button
              color="inherit"
              onClick={handleStatsClick}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              Stats
            </Button>
            <Menu
              anchorEl={statsAnchor}
              open={Boolean(statsAnchor)}
              onClose={handleStatsClose}
              {...MenuProps}
            >
              {statsItems.map((item) => (
                <MenuItem 
                  key={item.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <ListItemIcon sx={{ color: 'primary.main' }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label}
                    primaryTypographyProps={{
                      sx: { 
                        color: 'text.primary',
                        fontWeight: 500,
                      }
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>

            <Button
              variant="contained"
              startIcon={<AccountBalanceWalletIcon />}
              className="interact-button"
              sx={{
                backgroundColor: '#FF2E63',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 46, 99, 0.9)',
                  boxShadow: '0 0 20px rgba(255, 46, 99, 0.5)'
                }
              }}
            >
              Connect Wallet
            </Button>
          </Box>
        )}

        {/* Mobile Menu Drawer */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={toggleMobileMenu}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: 300,
              background: 'background.paper',
              p: 2,
            }
          }}
        >
          <List>
            {/* Search Bar in Mobile Menu */}
            <ListItem sx={{ mb: 2 }}>
              <Search sx={{ width: '100%' }}>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <SearchBar StyledInputBase={StyledInputBase} />
              </Search>
            </ListItem>

            {/* Mobile Menu Items */}
            {mobileMenuItems.map((section) => (
              <Box key={section.label} sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    px: 2,
                    mb: 1,
                  }}
                >
                  {section.label}
                </Typography>
                {section.items.map((item) => (
                  <MenuItem
                    key={item.label}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: 1,
                      cursor: 'default',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        sx: {
                          color: 'text.primary',
                          fontWeight: 500,
                        }
                      }}
                    />
                  </MenuItem>
                ))}
              </Box>
            ))}

            {/* Connect Wallet Button in Mobile Menu */}
            <ListItem sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AccountBalanceWalletIcon />}
                className="interact-button"
                sx={{
                  backgroundColor: '#FF2E63',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 46, 99, 0.9)',
                    boxShadow: '0 0 20px rgba(255, 46, 99, 0.5)'
                  }
                }}
              >
                Connect Wallet
              </Button>
            </ListItem>
          </List>
        </Drawer>
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;
